import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';

import { ConnectionNode } from '@voximplant/websdk';
import { LoggerLoader } from '@voximplant/websdk/modules/logger';
import { LogLevel } from '@voximplant/websdk/modules/logger';
import { clientToken } from '@voximplant/websdk/modules/client';
import { MessagingLoader } from '@voximplant/websdk/modules/messaging';
import { ConferenceLoader } from '@voximplant/websdk/modules/conference-manager';
import { StreamLoader } from '@voximplant/websdk/modules/stream';
import { makeGetRequest } from "@/api/helpers";
import { getIsDemo } from '@/utils/env';
import { initializeCore } from '@/utils/voximplantCore';

// --- Типы ---
enum AuthState {
    NoAuth,
    Auth,
    Processing,
}

interface AuthStoreData {
    username: string,
    voxUserId: string,
}

@Module({ dynamic: true, store: store, name: 'voxAuth' })
class Auth extends VuexModule {
    public token: string | null = null;
    public username: string | null = null;
    public voxUserId: string | null = null;
    public authState: AuthState = AuthState.NoAuth;

    // --- Мутации ---
    @Mutation
    setAuthData(payload: Partial<AuthStoreData> & { authState: AuthState }) {
        this.username = payload.username || null;
        this.voxUserId = payload.voxUserId || null;
        this.authState = payload.authState;
    }

    @Mutation
    setAuthState(state: AuthState) {
        this.authState = state;
    }

    @Mutation
    setToken(token: string | null) {
        this.token = token;
    }

    // --- Actions ---
    @Action({ rawError: true })
    async makeAuth() {
        this.context.commit('setAuthState', AuthState.Processing);

        const response = await makeGetRequest('/api/auth/get-vox-token');

        if (response.data.result) {
            await this.context.dispatch('authSDK', { username: response.data.result.vox_auth_username });

            const payload = {
                username: response.data.result.vox_auth_username,
                voxUserId: response.data.result.vox_user_id,
                authState: AuthState.Auth,
            };

            this.context.commit('setAuthData', payload);
        } else {
            this.context.commit('setAuthState', AuthState.NoAuth);
            throw new Error('Not auth');
        }
    }

    @Action({ rawError: true })
    async authSDK({ username }: { username: string }) {
        const core = initializeCore({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            logger: LoggerLoader({
                logLevel: LogLevel.Debug,
            }),
        });

        core.registerModules([
            StreamLoader(),
            MessagingLoader(),
            ConferenceLoader(),
        ]);

        const client = core.getModule(clientToken);

        if (! client) {
            throw new Error('Client module not found');
        }

        await client.connect({
            node: getIsDemo() ? ConnectionNode.NODE_2 : ConnectionNode.NODE_3,
        });

        const oneTimeKey = await client.requestOneTimeKey({ username });

        const response = await makeGetRequest('/api/auth/get-vox-token?one_time_key=' + oneTimeKey);
        const hash = response?.data?.result?.vox_token || '0';

        await client.loginOneTimeKey({
            username,
            hash,
        });
    }
}

export default getModule(Auth);
export { AuthState };
